// You can write more code here

/* START OF COMPILED CODE */

import BaseScene from '../core/scenes/BaseScene';
/* START-USER-IMPORTS */
import { app } from '../core/App';
import { GameSceneData, SceneData, SceneId, TransitionPoint } from '../app-routing';
import GameUI from './GameUI';
import { GameDataHandler } from '../GameDataHandler';
import { LevelManager } from './../level/LevelManager';
import { ParallaxController } from '../core/modules/Parallax/ParallaxController';
import { RunManager } from './../level/RunManager';
import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { RunSettings, RunSettingsHandler } from '../level/RunSettingsHandler';
import { CharacterName } from './../Character';
import { Level, LevelName } from './../level/levels/level';
import { LevelUtil } from '../level/levels/LevelUtil';
import { ThemeUtil } from '../level/themes/ThemeUtil';
import { SplatEffectsLayer } from './../SplatEffectsLayer';
import { ObstacleEffectController as ObstacleEffectController } from '../level/ObstacleEffectController';
import { Theme, Weather, WeatherValues } from '../level/themes/theme';
import { gameLevelAssetsToLoad as gameLevelAssetsToPreload } from '../assets-to-load';
/* END-USER-IMPORTS */

export default class Game extends BaseScene {
  constructor() {
    super('Game');

    /* START-USER-CTR-CODE */
    this.hasGameCreateFinished = false;
    this.hasGameUICreateFinished = false;
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {
    // BackgroundLayer
    const backgroundLayer = this.add.layer();

    // FloorLayer
    const floorLayer = this.add.layer();

    // VehicleLayer
    const vehicleLayer = this.add.layer();

    // ForegroundLayer
    const foregroundLayer = this.add.layer();

    // ObstacleEffectLayer
    const obstacleEffectLayer = this.add.layer();

    // GameUILayer
    const gameUILayer = this.add.layer();

    // WeatherLayer
    const weatherLayer = this.add.layer();

    // SplatLayer
    const splatLayer = this.add.layer();

    this.backgroundLayer = backgroundLayer;
    this.floorLayer = floorLayer;
    this.vehicleLayer = vehicleLayer;
    this.foregroundLayer = foregroundLayer;
    this.obstacleEffectLayer = obstacleEffectLayer;
    this.gameUILayer = gameUILayer;
    this.weatherLayer = weatherLayer;
    this.splatLayer = splatLayer;

    this.events.emit('scene-awake');
  }

  private backgroundLayer!: Phaser.GameObjects.Layer;
  private floorLayer!: Phaser.GameObjects.Layer;
  private vehicleLayer!: Phaser.GameObjects.Layer;
  private foregroundLayer!: Phaser.GameObjects.Layer;
  private obstacleEffectLayer!: Phaser.GameObjects.Layer;
  private gameUILayer!: Phaser.GameObjects.Layer;
  private weatherLayer!: Phaser.GameObjects.Layer;
  private splatLayer!: Phaser.GameObjects.Layer;

  /* START-USER-CODE */
  private sceneData!: SceneData;
  private gameSceneData!: GameSceneData;
  private runSettings!: RunSettings;
  private gameUI!: GameUI;
  private matterUpdater!: MatterPhysicsUpdater;
  private runManager!: RunManager;
  private levelManager!: LevelManager;
  private splatEffectsLayer!: SplatEffectsLayer;
  private obstacleEffectController!: ObstacleEffectController;
  private parallaxController!: ParallaxController;
  private particles!: Phaser.GameObjects.Particles.ParticleEmitterManager;

  protected hasGameCreateFinished: boolean;
  protected hasGameUICreateFinished: boolean;
  protected level!: Level;
  protected theme!: Theme;

  // Allows game to wait for gameUI to be ready.
  set HasGameUICreateFinished(hasFinished: boolean) {
    this.hasGameUICreateFinished = hasFinished;
  }

  public init(data: SceneData) {
    super.init(data);

    let gameSceneDataDefaults: GameSceneData = {
      shouldSkip: false
    };

    let runSettingsDefaults: RunSettings = {
      selectedCharacter: CharacterName.Tom,
      selectedLevel: LevelName.Level1,
      levelPurchaseDetails: LevelUtil.defaultLevelPurchaseDetails,
      instantRestart: false,
      regenOnRestart: true
    };

    this.sceneData = data;
    this.gameSceneData = Object.assign({}, gameSceneDataDefaults, data.gameSceneData);
    this.runSettings = Object.assign({}, runSettingsDefaults, data.runSettings);

    this.registry.set(GameDataHandler.keys.Score, 0);
    this.registry.set(GameDataHandler.keys.Distance, 0);
    this.matter.world.autoUpdate = false;

    this.hasGameCreateFinished = false;
    this.hasGameUICreateFinished = false;

    this.level = LevelUtil.getLevelFromName(this.runSettings.selectedLevel);
    this.theme = ThemeUtil.getTheme(this.level.theme);
    LevelUtil.generateMeasurements(this.theme.roadDetails);

    // Try loading the assets needed for the level's theme.
    if (this.theme) {
      let assetsToPreload = gameLevelAssetsToPreload(this, this.theme?.id);
      app.AssetManager.addDynamicAssets(assetsToPreload);
    }
  }

  public preload() {
    super.preload();

    // this.load.pack('asset-pack-game', assetPack as any);

    this.load.on(Phaser.Loader.Events.COMPLETE, this.postload, this);
  }

  public postload() {
    super.postload();
  }

  public create() {
    super.create();
    this.editorCreate();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.destroy, this);

    this.showGameUI();

    this.setupGame();

    this.runManager.initRun(this.runSettings, this.level, this.theme);
    this.showWeather(this.theme.weather);

    this.hasGameCreateFinished = true;
    this.start();
  }

  public start() {
    if (!this.hasGameCreateFinished || !this.hasGameUICreateFinished) return;

    // if((this.level.id=="level2")&&(GameDataHandler.getHighScore(this, this.runSettings.selectedLevel)<=0)){
    //   this.gameUI.showHelp();
    // }
    // else{
      this.startGame();
    // }

    if (this.gameSceneData.shouldSkip) this.time.delayedCall(100, () => this.runOver());
  }

  public shutdown() {
    if (this?.gameUI?.scene?.isActive() || this?.gameUI?.scene?.isPaused()) this.gameUI.scene.stop();

    this.events.off(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    this.gameUI.events.off(Phaser.Scenes.Events.CREATE, this.gameUI.showUI, this.gameUI);

    this.events.off(RunManager.events.RunOver, this.runOver, this);

    this?.parallaxController?.clearLayers();

    super.shutdown();
  }

  public destroy() {
    super.destroy();
  }

  public pause() {
    this.scene.pause();
  }

  public resume() {
    this.scene.resume();
  }

  public introHelpComplete(){
    this.scene.resume();
    this.startGame();
  }

  public back() {
    // this.player.freeze();
    app.gotoNextScene(this, TransitionPoint.GameBack, this.sceneData);
  }

  public showWeather(weather: WeatherValues) {
    if (weather == Weather.Snow) this.makeSnowParticles();
  }

  protected runOver() {
    // this.player.freeze();
    this.finish();
  }

  protected finish() {
    this.gameUI.hideUI();

    let score = Math.trunc(this.registry.get(GameDataHandler.keys.Score));
    // let highScore = this.registry.get(GameDataHandler.keys.HighScore);
    let highScore = GameDataHandler.getHighScore(this, this.runSettings.selectedLevel);
    let earnedPoints = this.registry.get(GameDataHandler.keys.EarnedPoints);

    // if (score > highScore) this.registry.set(GameDataHandler.keys.HighScore, score);
    if (score > highScore) GameDataHandler.setHighScore(this, this.runSettings.selectedLevel, score);
    this.registry.inc(GameDataHandler.keys.EarnedPoints, score);

    this.gameUI.showAudioBtn();
    this.gameUI.showEndScreen(score, highScore, earnedPoints);
  }

  protected showGameUI() {
    this.scene.run(SceneId.GameUI, this.sceneData);
    this.gameUI = this.scene.get(SceneId.GameUI) as GameUI;
    this.gameUI.events.once(Phaser.Scenes.Events.CREATE, this.gameUI.showUI, this.gameUI);
  }

  // One time setup for the scene.
  protected setupGame() {
    this.matter.world.setGravity(0, 0, 0.002);

    let zoom = this.game.device.os.desktop ? 1 : 1.25;
    this.cameras.main.setZoom(zoom, zoom);
    // this.cameras.main.setZoom(1, 1);

    let cameraDefaultY = -384;
    let cameraOffsetYDesktop = 0;
    let cameraOffsetYMobile = 80;
    let cameraY = cameraDefaultY + (this.game.device.os.desktop ? cameraOffsetYDesktop : cameraOffsetYMobile);
    let cameraYScaled = cameraY * (1 / this.cameras.main.zoomY);
    let boundsY = cameraYScaled - cameraY;

    this.matterUpdater = new MatterPhysicsUpdater(this);
    this.parallaxController = new ParallaxController(this, this.cameras.main, this.backgroundLayer, {
      globalOffsetY: boundsY + (this.game.device.os.desktop ? cameraOffsetYDesktop : cameraOffsetYMobile)
    });

    let defaultLevel = LevelUtil.getLevelFromName(LevelName.Level1);
    let defaultTheme = ThemeUtil.getTheme(defaultLevel.theme);

    this.levelManager = new LevelManager(this, defaultLevel, defaultTheme);

    this.splatEffectsLayer = new SplatEffectsLayer(this, this.splatLayer, this.levelManager);
    this.obstacleEffectController = new ObstacleEffectController(this, this.obstacleEffectLayer);

    this.runManager = new RunManager(
      this,
      this.levelManager,
      this.parallaxController,
      this.matterUpdater,
      this.cameras.main,
      this.vehicleLayer,
      this.gameUILayer,
      {
        cameraBoundsY: cameraYScaled
      }
    );

    this.levelManager.init(this.runManager.TrafficManager, this.floorLayer, this.foregroundLayer);

    this.events.on(RunManager.events.RunOver, this.runOver, this);
  }

  protected startGame() {
    console.log("START");
    let randomDelayToStart = 0; //RandomShare.Between(1000, 2000);
    this.time.delayedCall(1000, this.gameUI.showTaxiCard, undefined, this.gameUI);
    this.time.delayedCall(2400, this.gameUI.lowerTrafficLight, undefined, this.gameUI);
    this.time.delayedCall(2900, this.gameUI.changeTrafficLightToAmber, undefined, this.gameUI);
    this.time.delayedCall(3400 + randomDelayToStart, this.gameUI.changeTrafficLightToGreen, undefined, this.gameUI);
    this.time.delayedCall(3400 + randomDelayToStart, this.runManager.startRun, undefined, this.runManager);
    this.time.delayedCall(3600 + randomDelayToStart, this.gameUI.raiseTrafficLight, undefined, this.gameUI);
  }

  protected makeSnowParticles() {
    let line = new Phaser.Geom.Line(0, -100, 1920, -100);
    this.particles = this.make.particles(
      {
        key: 'common',
        emitters: [
          {
            name: 'snow',
            frame: ['circle-white-small.png'],
            angle: { min: 45, max: 180 - 45 },
            scale: { min: 0.15, max: 0.65 },
            speed: { min: 100, max: 200 },
            gravityY: 100,
            lifespan: 4000,
            quantity: 1,
            emitZone: { source: line }
          }
        ]
      },
      false
    );
    this.weatherLayer.add(this.particles);

    let emitter = this.particles.emitters.getByName('snow');
    emitter?.setScrollFactor(0, 0);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */
